// Vue & Plugins
import { MaybeRefOrGetter, toValue } from 'vue'

// Composables
import {
  type QueryFilters,
  type QueryKey,
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/vue-query'

// Models
import { Pagination } from '../../common/models/pagination'
import { ToastEvent } from '../../common/models/toastEvent'
import { type SiteID } from '../../sites/models/site'
import { Tag } from '../models/tag'

// Services
import { useEventStore } from '../../common/eventStore'

import {
  TagAttachForm,
  TagCreateForm,
  TagSearchForm,
  attach,
  create,
  detach,
  fetch,
  fetchForTaggable,
} from '../tagService'

interface TagFilters {
  name?: MaybeRefOrGetter<string>
  types?: MaybeRefOrGetter<string[]>

  taggableId?: MaybeRefOrGetter<string>
  taggableType?: MaybeRefOrGetter<'employee' | 'shift'>
  withoutTaggableId?: MaybeRefOrGetter<string | undefined>

  perPage?: MaybeRefOrGetter<number>
  page?: MaybeRefOrGetter<number>
}

export const tagKeys = {
  all: ['tags'] as const,
  lists: () => [...tagKeys.all, 'list'] as const,
  list: (
    siteId: MaybeRefOrGetter<SiteID | null | undefined>,
    filters: TagFilters,
  ) =>
    [
      ...tagKeys.lists(),
      siteId,
      {
        name: filters.name,
        types: filters.types,
        taggableId: filters.taggableId,
        taggableType: filters.taggableType,
        withoutTaggableId: filters.withoutTaggableId,
        perPage: filters.perPage,
        page: filters.page,
      },
    ] as const,
}

const filtersToForm = (filters: TagFilters): TagSearchForm => {
  return {
    name: toValue(filters.name),
    types: toValue(filters.types),

    taggableId: toValue(filters.taggableId),
    taggableType: toValue(filters.taggableType),
    withoutTaggableId: toValue(filters.withoutTaggableId),

    perPage: toValue(filters.perPage ?? 15),
    page: toValue(filters.page ?? 1),
  }
}

export function useTagQuery(
  siteId: MaybeRefOrGetter<SiteID | null | undefined>,
  filters: TagFilters = {},
  options: {
    enabled?: MaybeRefOrGetter<boolean>
    /** How long a fetched list is reused before it is fetched again */
    staleTime?: number
  } = {},
) {
  return useQuery({
    queryKey: tagKeys.list(siteId, filters),
    queryFn: async () => {
      const form = filtersToForm(filters)

      return form.taggableId && form.taggableType
        ? await fetchForTaggable(toValue(siteId)!, form)
        : await fetch(toValue(siteId)!, form)
    },
    placeholderData: keepPreviousData,
    enabled: () => toValue(options.enabled ?? true) && !!toValue(siteId),
    staleTime: options.staleTime,
  })
}

export function useTagCreateMutation(siteId: MaybeRefOrGetter<SiteID>) {
  const eventStore = useEventStore()
  const queryClient = useQueryClient()

  return useMutation<{ tag: Tag }, Error, { form: TagCreateForm }>({
    mutationFn: async (variables) => {
      return create(toValue(siteId), variables.form)
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: tagKeys.lists(),
        refetchType: 'none',
      })
    },
    onSuccess: async () => {
      const event = new ToastEvent('Saved')
      eventStore.addEvent(event)
    },
  })
}

type TagList = { tags: Tag[]; pagination: Pagination }

/** The cached lists of one record's tags, which attaching and detaching change at once */
const listsOf = (form: TagAttachForm): QueryFilters => ({
  queryKey: tagKeys.lists(),
  predicate: (query) => {
    const filters = query.queryKey[3] as Partial<TagSearchForm> | undefined

    return (
      filters?.taggableId === form.targetId &&
      filters?.taggableType === form.targetType
    )
  },
})

type Snapshot = { previous: [QueryKey, TagList | undefined][] }

export function useTagAttachMutation(siteId: MaybeRefOrGetter<SiteID>) {
  const queryClient = useQueryClient()

  return useMutation<void, Error, { form: TagAttachForm; tag?: Tag }, Snapshot>(
    {
      mutationFn: async (variables) => {
        return attach(toValue(siteId), variables.form)
      },
      // The chip shows straight away when the caller knows the tag
      onMutate: async ({ form, tag }) => {
        if (!tag) return { previous: [] }

        await queryClient.cancelQueries({ queryKey: tagKeys.lists() })
        const previous = queryClient.getQueriesData<TagList>(listsOf(form))

        queryClient.setQueriesData<TagList>(listsOf(form), (data) =>
          data && !data.tags.some((t) => t.id === tag.id)
            ? { ...data, tags: [...data.tags, tag] }
            : data,
        )

        return { previous }
      },
      onError: (_error, _variables, context) => {
        context?.previous.forEach(([key, data]) =>
          queryClient.setQueryData(key, data),
        )
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: tagKeys.lists(),
          refetchType: 'none',
        })
      },
    },
  )
}

export function useTagDetachMutation(siteId: MaybeRefOrGetter<SiteID>) {
  const queryClient = useQueryClient()

  return useMutation<void, Error, { form: TagAttachForm }, Snapshot>({
    mutationFn: async (variables) => {
      return detach(toValue(siteId), variables.form)
    },
    // The chip goes straight away
    onMutate: async ({ form }) => {
      await queryClient.cancelQueries({ queryKey: tagKeys.lists() })
      const previous = queryClient.getQueriesData<TagList>(listsOf(form))

      queryClient.setQueriesData<TagList>(listsOf(form), (data) =>
        data
          ? { ...data, tags: data.tags.filter((t) => t.name !== form.tag) }
          : data,
      )

      return { previous }
    },
    onError: (_error, _variables, context) => {
      context?.previous.forEach(([key, data]) =>
        queryClient.setQueryData(key, data),
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: tagKeys.lists(),
        refetchType: 'none',
      })
    },
  })
}
